import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import LazyImage from "../components/LazyImage";
import ProjectCard from "../components/ProjectCard";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const ROOMS = ["Bathroom", "Kitchen", "Living Room", "Pool Area", "Hotel", "Facade", "Restaurant", "Outdoor"];
const STYLES = ["Modern Minimalist", "Scandinavian", "Industrial", "Tropical Luxury", "Hotel Style", "Mediterranean", "Japandi", "Contemporary", "Mid-Century Modern", "Art Deco"];

export default function Home() {
    const [hero, setHero] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [latest, setLatest] = useState([]);
    const [heroIdx, setHeroIdx] = useState(0);

    useEffect(() => {
        let ignore = false;
        (async () => {
            const [h, f, l] = await Promise.all([
                api.get("/projects/hero"),
                api.get("/projects/featured", { params: { limit: 6 } }),
                api.get("/projects", { params: { limit: 9 } }),
            ]);
            if (ignore) return;
            setHero(h.data || []);
            setFeatured(f.data || []);
            setLatest((l.data?.items || []).slice(0, 9));
        })();
        return () => { ignore = true; };
    }, []);

    useEffect(() => {
        if (hero.length < 2) return;
        const t = setInterval(() => setHeroIdx((i) => (i + 1) % hero.length), 6500);
        return () => clearInterval(t);
    }, [hero]);

    const heroProj = hero[heroIdx];

    return (
        <main data-testid="home-page" className="bg-black text-white">
            {/* HERO */}
            <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden">
                <AnimatePresence mode="sync">
                    {heroProj && (
                        <motion.div
                            key={heroProj.id}
                            initial={{ opacity: 0, scale: 1.06 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute inset-0"
                        >
                            <LazyImage src={heroProj.thumbnail_url} className="w-full h-full" />
                            <div className="absolute inset-0 hero-overlay" />
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="relative h-full container-luxury flex flex-col justify-end pb-24 pt-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="overline mb-6 text-gold">A Curated Virtual Showroom</div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight text-shadow-soft">
                            Where every surface
                            <br />
                            tells a <em className="text-gold not-italic">timeless</em> story.
                        </h1>
                        <p className="mt-8 text-white/75 max-w-xl leading-relaxed text-base sm:text-lg">
                            Twelve completed projects from three premium tile houses — assembled into one cinematic catalog for architects, designers, and clients.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/explore" className="btn-gold" data-testid="hero-cta-explore"><span>Enter the Showroom</span><ArrowRight size={16} /></Link>
                            <Link to="/companies" className="btn-ghost-gold" data-testid="hero-cta-companies">Meet the Houses</Link>
                        </div>
                    </motion.div>

                    <div className="mt-12 flex items-center gap-3" data-testid="hero-pagination">
                        {hero.map((_, i) => (
                            <button
                                key={i}
                                aria-label={`Hero ${i + 1}`}
                                onClick={() => setHeroIdx(i)}
                                className={`h-px transition-all duration-700 ${i === heroIdx ? "w-16 bg-gold" : "w-8 bg-white/30"}`}
                            />
                        ))}
                        {heroProj && (
                            <div className="ml-6 hidden sm:flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/70">
                                <span>{heroProj.company_name}</span>
                                <span className="text-gold">·</span>
                                <span>{heroProj.room_type}</span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* MOOD MARQUEE */}
            <section className="border-y border-white/10 bg-[#070707] py-6 overflow-hidden">
                <div className="marquee whitespace-nowrap flex gap-12 text-white/40 font-serif italic text-2xl">
                    {[...STYLES, ...STYLES].map((s, i) => (
                        <Link key={i} to={`/explore?style=${encodeURIComponent(s)}`} className="hover:text-gold transition-colors duration-500">
                            {s} <span className="mx-6 text-gold/50">✦</span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED — Bento */}
            <section className="container-luxury py-24 lg:py-32" data-testid="featured-section">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="overline mb-4">The Curated Collection</div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[1.05]">
                            Featured installations from the houses we represent.
                        </h2>
                    </div>
                    <Link to="/explore?featured=true" className="text-gold tracking-[0.2em] uppercase text-xs hover:text-gold-soft inline-flex items-center gap-2">
                        View all <ArrowUpRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    {featured.map((p, i) => {
                        // bento spans
                        const span = i === 0 ? "col-span-12 md:col-span-7 row-span-2" : i === 1 ? "col-span-12 md:col-span-5" : i === 2 ? "col-span-12 md:col-span-5" : "col-span-12 sm:col-span-6 md:col-span-4";
                        const ratio = i === 0 ? "aspect-[4/5] md:aspect-[5/6]" : i === 1 || i === 2 ? "aspect-[5/4] md:aspect-[6/5]" : "aspect-[4/5]";
                        return (
                            <Link
                                key={p.id}
                                to={`/projects/${p.slug}`}
                                className={`group relative overflow-hidden ${span}`}
                                data-testid={`featured-card-${p.slug}`}
                            >
                                <div className={`overflow-hidden ${ratio}`}>
                                    <div className="w-full h-full transform transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]">
                                        <LazyImage src={p.thumbnail_url} alt={p.title} className="w-full h-full" />
                                    </div>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                                    <div className="overline text-gold mb-2">{p.room_type} · {p.company_name}</div>
                                    <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight max-w-md">{p.title}</h3>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ROOM CATEGORIES */}
            <section className="bg-[#070707] py-24 lg:py-32 border-y border-white/5" data-testid="rooms-section">
                <div className="container-luxury">
                    <div className="overline mb-4">Browse by Application</div>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mb-14">Find the right surface for every space.</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
                        {ROOMS.map((r, i) => (
                            <Link
                                key={r}
                                to={`/explore?room=${encodeURIComponent(r)}`}
                                data-testid={`room-tile-${r.toLowerCase().replace(/\s+/g,'-')}`}
                                className="bg-black p-8 md:p-10 group hover:bg-[#0c0c0c] transition-colors duration-500 flex flex-col justify-between min-h-[180px]"
                            >
                                <span className="text-white/40 text-xs tracking-[0.3em] uppercase">{String(i + 1).padStart(2, "0")}</span>
                                <span className="font-serif text-2xl md:text-3xl tracking-tight group-hover:text-gold transition-colors duration-500">{r}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* LATEST — masonry preview */}
            <section className="container-luxury py-24 lg:py-32" data-testid="latest-section">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="overline mb-4">Recently Completed</div>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Fresh from the field.</h2>
                    </div>
                    <Link to="/explore" className="text-gold tracking-[0.2em] uppercase text-xs hover:text-gold-soft inline-flex items-center gap-2">
                        Open Inspiration Explorer <ArrowUpRight size={14} />
                    </Link>
                </div>
                <div className="masonry">
                    {latest.map((p, i) => (
                        <ProjectCard key={p.id} project={p} index={i} />
                    ))}
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="container-luxury py-24" data-testid="cta-section">
                <div className="border border-white/10 p-12 md:p-20 text-center">
                    <div className="gold-divider w-32 mx-auto mb-8" />
                    <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto leading-[1.05]">
                        Considering a project of your own?
                    </h2>
                    <p className="mt-6 text-white/65 max-w-xl mx-auto">
                        Connect with one of our specialists. We'll match the right house and the right surface to your vision.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="btn-gold" data-testid="cta-contact">Start a Conversation</Link>
                        <Link to="/tv" className="btn-ghost-gold" data-testid="cta-tv">Showroom TV Mode</Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
